import { z } from 'zod'
import { CARD_COLOUR_HEX, ICONS, LIMITS, STATUS_LABELS, type ItemIcon, type ItemStatus } from './constants.js'
import { newId, nowIso } from './ids.js'
import type { BoardAccess } from './access.js'
import { linkSyncSchema, newNodeCore, nodeCoreFields, relationshipProblem, titleSchema, type NodeCore } from './schemas.js'

/**
 * Mindmaps: a second kind of board, stored as one tree. Ported from the standalone
 * Electron app (~/Projects/mindmap), whose JSON file format is kept for import and export.
 */

/**
 * The legacy app's status keys, used only in its file format. Our statuses map onto them;
 * the two it lacks (New and Blocked) are written as the nearest it has.
 */
const LEGACY_TO_STATUS = { todo: 'planned', inprogress: 'in_progress', testing: 'testing', done: 'done' } as const satisfies Record<string, ItemStatus>
type LegacyStatus = keyof typeof LEGACY_TO_STATUS
const STATUS_TO_LEGACY: Record<ItemStatus, LegacyStatus> = {
  new: 'todo',
  planned: 'todo',
  in_progress: 'inprogress',
  testing: 'testing',
  blocked: 'inprogress',
  done: 'done',
}

/**
 * The swatches the colour picker offers: the card palette, the larger of the two, so a
 * node and the cards made from it are the same colour.
 */
export const MINDMAP_SWATCHES: readonly string[] = Object.values(CARD_COLOUR_HEX)

/** New top-level branches take the next swatch in turn. */
export const MINDMAP_BRANCH_COLOURS = MINDMAP_SWATCHES

export const MINDMAP_ROOT_COLOUR = '#6c5ce7'

export const MINDMAP_LIMITS = {
  title: LIMITS.cardTitle,
  description: LIMITS.description,
  nodes: 5_000,
} as const

const hexColourSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a #rrggbb colour')

/** A node: the fields every item shares, plus its place in the tree and how it is drawn. */
export interface MindmapNode extends NodeCore {
  children: MindmapNode[]
  collapsed: boolean
  /** Null inherits the nearest ancestor's colour. Any #rrggbb, so legacy files keep theirs. */
  colour: string | null
  /** Manual offset from the auto-layout position, in world units. */
  offsetX: number
  offsetY: number
}

export const mindmapNodeSchema: z.ZodType<MindmapNode> = z.lazy(() =>
  z.object({
    ...nodeCoreFields,
    children: z.array(mindmapNodeSchema),
    collapsed: z.boolean(),
    colour: hexColourSchema.nullable(),
    offsetX: z.number().finite(),
    offsetY: z.number().finite(),
  }),
)

/** A whole tree, checked that every relationship points at another node of it. */
const mindmapRootSchema = mindmapNodeSchema.superRefine((root, ctx) => {
  const problem = relationshipProblem(mindmapNodes(root))
  if (problem) ctx.addIssue({ code: 'custom', message: problem })
})

/** Wire shape for a whole mindmap: `GET /api/mindmaps/:id`. */
export const mindmapSchema = z
  .object({
    id: z.uuid(),
    name: titleSchema,
    revision: z.number().int().min(0),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
    root: mindmapNodeSchema,
  })
  .refine((map) => countNodes(map.root) <= MINDMAP_LIMITS.nodes, {
    message: `A mindmap is limited to ${MINDMAP_LIMITS.nodes} nodes`,
    path: ['root'],
  })

export type Mindmap = z.infer<typeof mindmapSchema>

/** A mindmap as the API returns it: with how the asking user reaches it. */
export type MindmapView = Mindmap & { access: BoardAccess }

/** `POST /api/mindmaps`: a client-generated id, so a lost response can be retried; an import passes the tree. */
export const mindmapCreateSchema = z.object({
  id: z.uuid(),
  name: titleSchema,
  root: mindmapRootSchema.optional(),
  /** The project to create it in; the user's default project when left out. */
  projectId: z.uuid().optional(),
})

/** `PUT /api/mindmaps/:id`: the whole tree, against the revision the client last saw. */
export const mindmapSaveSchema = z.object({
  expectedRevision: z.number().int().min(0),
  name: titleSchema.optional(),
  root: mindmapRootSchema,
  sync: linkSyncSchema.optional(),
})

export type MindmapSave = z.infer<typeof mindmapSaveSchema>

/** What has been generated from a mindmap: boards (by branch) and their cards (by node). */
export interface MindmapLinks {
  boards: { id: string; name: string; nodeId: string; access: BoardAccess }[]
  cards: { boardId: string; cardId: string; title: string; nodeId: string }[]
}

// --- tree helpers ---------------------------------------------------------------

export function newMindmapNode(title: string, colour: string | null = null): MindmapNode {
  return { ...newNodeCore(title), children: [], collapsed: false, colour, offsetX: 0, offsetY: 0 }
}

/** A new mindmap: a root named after the map, with no branches. */
export function createMindmap(input: { id?: string; name: string; root?: MindmapNode; now?: string }): Mindmap {
  const now = input.now ?? nowIso()
  const name = input.name.trim()
  return {
    id: input.id ?? newId(),
    name,
    revision: 0,
    createdAt: now,
    updatedAt: now,
    root: input.root ?? newMindmapNode(name || 'Central idea', MINDMAP_ROOT_COLOUR),
  }
}

export function countNodes(node: MindmapNode): number {
  let count = 1
  for (const child of node.children) count += countNodes(child)
  return count
}

export function findMindmapNode(root: MindmapNode, id: string): MindmapNode | null {
  if (root.id === id) return root
  for (const child of root.children) {
    const found = findMindmapNode(child, id)
    if (found) return found
  }
  return null
}

export function findMindmapParent(root: MindmapNode, id: string): MindmapNode | null {
  for (const child of root.children) {
    if (child.id === id) return root
    const found = findMindmapParent(child, id)
    if (found) return found
  }
  return null
}

/** Every node id in the tree, root first. */
export function mindmapNodeIds(root: MindmapNode): string[] {
  const ids: string[] = []
  const walk = (node: MindmapNode) => {
    ids.push(node.id)
    node.children.forEach(walk)
  }
  walk(root)
  return ids
}

/** Every node in the tree, root first. */
export function mindmapNodes(root: MindmapNode): MindmapNode[] {
  const nodes: MindmapNode[] = []
  const walk = (node: MindmapNode) => {
    nodes.push(node)
    node.children.forEach(walk)
  }
  walk(root)
  return nodes
}

/** The colour a node is drawn with: its own, or the nearest ancestor's. */
export function effectiveColour(root: MindmapNode, id: string): string {
  const path: MindmapNode[] = []
  const walk = (node: MindmapNode): boolean => {
    path.push(node)
    if (node.id === id) return true
    for (const child of node.children) if (walk(child)) return true
    path.pop()
    return false
  }
  walk(root)
  for (let i = path.length - 1; i >= 0; i -= 1) {
    const colour = path[i]?.colour
    if (colour) return colour
  }
  return MINDMAP_ROOT_COLOUR
}

// --- legacy file format: import and export --------------------------------------

/** One node as the legacy app writes it. `done` mirrors `status === 'done'`. */
export interface LegacyMindmapNode {
  id: string
  text: string
  children: LegacyMindmapNode[]
  collapsed: boolean
  color: string | null
  note: string
  done: boolean
  status: LegacyStatus | null
  icon: ItemIcon | null
  offsetX: number
  offsetY: number
}

export class MindmapImportError extends Error {
  constructor(message: string, readonly path: string) {
    super(path ? `${message} (at ${path})` : message)
    this.name = 'MindmapImportError'
  }
}

/**
 * Parses a legacy (or Blueprint-exported) mindmap file. Lenient about what is missing —
 * optional fields are defaulted, unknown fields ignored — and strict about what is wrong.
 * Every node gets a fresh uuid, so importing the same file twice never collides.
 */
export function importLegacyMindmap(input: unknown): MindmapNode {
  let count = 0
  const parse = (data: unknown, path: string): MindmapNode => {
    count += 1
    if (count > MINDMAP_LIMITS.nodes) {
      throw new MindmapImportError(`A mindmap is limited to ${MINDMAP_LIMITS.nodes} nodes`, '')
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new MindmapImportError('Expected a node object', path || 'root')
    const raw = data as Record<string, unknown>
    const text = typeof raw.text === 'string' ? raw.text.trim() : ''
    if (!text) throw new MindmapImportError('Node text is missing', path ? `${path}.text` : 'text')
    const status =
      typeof raw.status === 'string' && raw.status in LEGACY_TO_STATUS
        ? LEGACY_TO_STATUS[raw.status as LegacyStatus]
        : raw.done === true
          ? 'done'
          : null
    const icon = typeof raw.icon === 'string' && (ICONS as readonly string[]).includes(raw.icon) ? (raw.icon as ItemIcon) : null
    const colour = typeof raw.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw.color) ? raw.color.toLowerCase() : null
    const children = raw.children === undefined ? [] : raw.children
    if (!Array.isArray(children)) throw new MindmapImportError('children must be a list', path ? `${path}.children` : 'children')
    const number = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0)
    return {
      ...newNodeCore(text.slice(0, MINDMAP_LIMITS.title)),
      description: typeof raw.note === 'string' ? raw.note.slice(0, MINDMAP_LIMITS.description) : '',
      status,
      icon,
      children: children.map((child, index) => parse(child, `${path ? `${path}.` : ''}children[${index}]`)),
      collapsed: raw.collapsed === true,
      colour,
      offsetX: number(raw.offsetX),
      offsetY: number(raw.offsetY),
    }
  }
  return parse(input, '')
}

/** The legacy file format, so exports open in the old app and import back unchanged. */
export function exportLegacyMindmap(node: MindmapNode): LegacyMindmapNode {
  return {
    id: node.id,
    text: node.title,
    children: node.children.map(exportLegacyMindmap),
    collapsed: node.collapsed,
    color: node.colour,
    note: node.description,
    done: node.status === 'done',
    status: node.status ? STATUS_TO_LEGACY[node.status] : null,
    icon: node.icon ?? null,
    offsetX: node.offsetX,
    offsetY: node.offsetY,
  }
}

/** A nested bullet outline: node text, with notes as indented paragraphs. */
export function mindmapToMarkdown(node: MindmapNode): string {
  const lines: string[] = []
  const walk = (current: MindmapNode, depth: number) => {
    const indent = '  '.repeat(depth)
    const status = current.status ? ` _(${STATUS_LABELS[current.status]})_` : ''
    lines.push(`${indent}- ${current.title}${status}`)
    const note = current.description.trim()
    if (note) {
      lines.push('')
      for (const line of note.split('\n')) lines.push(`${indent}  ${line}`)
      lines.push('')
    }
    for (const child of current.children) walk(child, depth + 1)
  }
  lines.push(`# ${node.title}`, '')
  const note = node.description.trim()
  if (note) lines.push(note, '')
  for (const child of node.children) walk(child, 0)
  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd()}\n`
}
